import { Constants } from '../constants'

/**
 * Combines the calendar day from `date` with the wall-clock time from `time`,
 * both read with local getters, and returns the resulting instant as an ISO
 * string with seconds and milliseconds zeroed.
 *
 * Replaces the previous UTC splice
 * (`date.toISOString()[0] + 'T' + time.toISOString()[1]`), which glued the UTC
 * day of one Date to the UTC time-of-day of another. For any user outside UTC
 * that mixed two different instants and could shift the reminder by an hour or a
 * whole day (e.g. a late-night pick in UTC-3 rolled the time-of-day to the next
 * UTC day while keeping the previous day). Reading local getters preserves the
 * exact local day + time the user selected.
 *
 * Zeroing the seconds keeps the value aligned with the same-minute duplicate
 * check in `MessagesServices.createReminder`, so the value we send matches the
 * minute window we query for duplicates.
 *
 * @example
 * // user in UTC-3 picking 22 Jun 2026, 17:30
 * resolveRemindAtISO(new Date(2026, 5, 22), timeAt(17, 30)) // '2026-06-22T20:30:00.000Z'
 */
// @spec FR-0041/AC-1@v2 — reminder fires at the user-picked time.
export function resolveRemindAtISO(date: Date, time: Date): string {
  const combined = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    0,
    0,
  )
  return combined.toISOString()
}

/**
 * True when `remindAtISO` is not far enough in the future to satisfy the
 * product minimum lead time (`Constants.reminder.minLeadTimeMs`). The floor sits
 * comfortably above GetStream's hard 60s rule so realistic latency / clock skew
 * between this check and the API call cannot push the value under the boundary.
 *
 * @example isReminderTooSoon(inSeconds(90)) // true — under the 2-minute floor
 */
// @spec FR-0041/AC-2@v2 — invalid reminder time guard.
export function isReminderTooSoon(remindAtISO: string, now: number = Date.now()): boolean {
  return new Date(remindAtISO).getTime() - now < Constants.reminder.minLeadTimeMs
}

/**
 * Maps an error thrown while creating a reminder to a user-friendly `Error`,
 * so the raw GetStream string ("remind_at must be at least one minute in the
 * future") never reaches the alert/toast UI. Defense-in-depth behind
 * `isReminderTooSoon` for the rare case server-side skew still rejects a value
 * that passed the client floor. Unknown errors pass through unchanged.
 *
 * @example toFriendlyReminderError(new Error('... "remind_at must be at least one minute in the future"'))
 */
export function toFriendlyReminderError(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error ?? '')
  if (/remind_at must be at least one minute in the future/i.test(message)) {
    return new Error(Constants.reminder.tooSoonMessage)
  }
  if (error instanceof Error) return error
  return new Error('Error while trying to create the reminder')
}
